import { useEffect, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { useToast } from "../toast";
import { MGMT } from "../env";
import type { AuthMethod } from "../types";

interface Props {
  open: boolean;
  driverId: number;
  method: AuthMethod | null;
  onClose: () => void;
  onSaved: () => void;
}

export default function AuthMethodDialog({ open, driverId, method, onClose, onSaved }: Props) {
  const api = useAuthApi();
  const toast = useToast();
  const isEditing = method !== null;

  const [identifier, setIdentifier] = useState("");
  const [label, setLabel] = useState("");
  const [status, setStatus] = useState("active");
  const [expiryDate, setExpiryDate] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (method) {
        setIdentifier(method.identifier ?? "");
        setLabel(method.label ?? "");
        setStatus(method.status);
        setExpiryDate(method.expiry_date ? method.expiry_date.slice(0, 10) : "");
      } else {
        setIdentifier("");
        setLabel("");
        setStatus("active");
        setExpiryDate("");
      }
    }
  }, [open, method]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isEditing) {
        await api(`${MGMT}/drivers/${driverId}/auth-methods/${method!.id}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            label: label || null,
            status,
            expiry_date: expiryDate || null,
          }),
        });
      } else {
        await api(`${MGMT}/drivers/${driverId}/auth-methods`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "rfid_card",
            identifier: identifier || null,
            label: label || null,
            status,
            expiry_date: expiryDate || null,
          }),
        });
      }
      toast.show(isEditing ? "Auth method updated" : "Auth method added");
      onSaved();
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{isEditing ? "Edit RFID Card" : "Add RFID Card"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {(isEditing ? method!.type !== "app" : true) && (
            <TextField
              label="RFID ID Tag"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              disabled={isEditing}
              fullWidth
              size="small"
            />
          )}

          <TextField
            label="Label (optional)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            fullWidth
            size="small"
          />

          <FormControl fullWidth size="small">
            <FormLabel sx={{ mb: 0.5 }}>Status</FormLabel>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="blocked">Blocked</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Expiry Date (optional)"
            type="date"
            value={expiryDate}
            onChange={(e) => setExpiryDate(e.target.value)}
            fullWidth
            size="small"
            InputLabelProps={{ shrink: true }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {isEditing ? "Save" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
